import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { EntityType } from "@mapmycustomers/shared/enum";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";

import useView from "@app/component/view/utils/useView";
import {
  getFunnelChartHidden,
  getListViewFunnelSummary,
  getListViewTotalFilteredRecordsCount,
} from "@app/scene/deal/store";
import { fetchFunnelStagesSummary } from "@app/scene/deal/store/actions";
import FunnelSummaryInfo from "@app/scene/deal/type/FunnelSummaryInfo";
import { getFunnels } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import TotalValues from "../../TotalValues";

import styles from "./DealListAggregateDataView.module.scss";
import FunnelChartContainer from "./FunnelChartContainer";

interface Props {
  funnels: Funnel[];
  funnelSummary: FunnelSummaryInfo;
  isFunnelChartHidden: boolean;
  onFetchFunnelStagesSummary: typeof fetchFunnelStagesSummary.request;
  subscribe: typeof subscribe;
  totalFilteredRecords: number;
  unsubscribe: typeof unsubscribe;
}

const DealListAggregateDataView: React.FC<Props> = ({
  funnels,
  funnelSummary,
  isFunnelChartHidden,
  onFetchFunnelStagesSummary,
  subscribe,
  totalFilteredRecords,
  unsubscribe,
}) => {
  const {
    viewState: { filter },
  } = useView();
  const selectedFunnelId = useMemo(() => {
    if (
      filter.funnel &&
      isSimpleCondition(filter.funnel) &&
      filter.funnel.operator === FilterOperator.IN_ANY &&
      filter.funnel.value.length === 1
    ) {
      return filter.funnel.value[0];
    }

    return;
  }, [filter]);
  const selectedFunnel = useMemo(
    () => funnels.find((funnel) => funnel.id === selectedFunnelId),
    [funnels, selectedFunnelId]
  );

  useEffect(() => {
    if (!isFunnelChartHidden && selectedFunnelId) {
      onFetchFunnelStagesSummary(selectedFunnelId);
    }
  }, [filter, isFunnelChartHidden, onFetchFunnelStagesSummary, selectedFunnelId]);

  useEffect(() => {
    const reloadData = () => {
      if (!isFunnelChartHidden && selectedFunnelId) {
        onFetchFunnelStagesSummary(selectedFunnelId);
      }
    };

    const listener = {
      onAdd: reloadData,
      onDelete: reloadData,
      onUpdate: reloadData,
    };

    subscribe({ entityTypes: [EntityType.DEAL], listener });

    return () => {
      unsubscribe({ entityTypes: [EntityType.DEAL], listener });
    };
  }, [isFunnelChartHidden, onFetchFunnelStagesSummary, selectedFunnelId, subscribe, unsubscribe]);

  if (selectedFunnelId && !isFunnelChartHidden) {
    return (
      <div className={styles.container}>
        <TotalValues
          classNames={{
            adjustedAmount: styles.adjustedAmount,
            container: styles.totalValuesContainer,
            dealsCount: styles.dealsCount,
            openAmount: styles.openAmount,
            values: styles.values,
          }}
          dealsCount={totalFilteredRecords}
          funnelSummary={funnelSummary}
          subtitle={selectedFunnel?.name}
        />
        <FunnelChartContainer funnelId={selectedFunnelId} />
      </div>
    );
  }

  return <TotalValues dealsCount={totalFilteredRecords} funnelSummary={funnelSummary} />;
};

const mapStateToProps = (state: RootState) => ({
  funnels: getFunnels(state),
  funnelSummary: getListViewFunnelSummary(state),
  isFunnelChartHidden: getFunnelChartHidden(state),
  totalFilteredRecords: getListViewTotalFilteredRecordsCount(state),
});

const mapDispatchToProps = {
  onFetchFunnelStagesSummary: fetchFunnelStagesSummary.request,
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealListAggregateDataView);
